'use client';

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Grid, Divider } from "@mui/material";
import Pagination from '@mui/material/Pagination';
import ProductCard from '@/app/components/cms_renderer/Product/index';
import FavoritesProvider from "./FavoritesProvider";
import { Product, ProductPublic } from "@/store/productSlice";
import { AUTH_LOGIN_EVENT } from "@nestingale/auth";

interface ProductGridProps {
    products: (Product | ProductPublic)[];
    productsPerPage: number;
    pageName: string;
    productCardHeight?: string;
    onProductClick?: (event: any, card?: any) => void;
    columns?: { xs: number; md: number };
    /** When true, pricing is fetched client-side after auth check (for dynamic-pricing brands). */
    dynamicPricing?: boolean;
    /** Product type — needed for the pricing API call. */
    productType?: string;
    /** Brand name — needed for the pricing API call. */
    brand?: string;
}

/**
 * Client island for paginated product grid with favorites.
 * Handles pagination state and favorites loading.
 *
 * When `dynamicPricing` is true, the grid receives minimal product data (image + name)
 * from SSR. Pricing, free-shipping, and "View in Your Room" are fetched client-side
 * via /api/products/pricing which validates the `nest_session` HttpOnly cookie
 * server-side. No client-side auth check is needed — the cookie is auto-sent by
 * the browser and the server returns 401 if the session is invalid/missing.
 */
const ProductGrid: React.FC<ProductGridProps> = ({
    products,
    productsPerPage,
    pageName,
    productCardHeight = "250px",
    onProductClick,
    columns = { xs: 12, md: 4 },
    dynamicPricing = false,
    productType,
    brand,
}) => {
    const [currentPageNumber, setCurrentPageNumber] = useState(1);

    // ── Dynamic pricing state ───────────────────────────────────────────
    const [pricingMap, setPricingMap] = useState<Record<string, any>>({});
    const [pricingLoaded, setPricingLoaded] = useState(!dynamicPricing);
    /** true when the pricing API returned 401 (user not logged in) */
    const [pricingDenied, setPricingDenied] = useState(false);
    /** Bumped to force a re-fetch (e.g. after login) */
    const [fetchTrigger, setFetchTrigger] = useState(0);

    /**
     * Fetch pricing data from the server.
     * The `nest_session` HttpOnly cookie is automatically included in the
     * request (same-origin). The server validates the cookie and returns
     * 401 if the user is not authenticated — no client-side auth check needed.
     *
     * Re-runs when `fetchTrigger` changes (post-login re-fetch).
     */
    useEffect(() => {
        console.log('[ProductGrid] useEffect triggered for pricing fetch. dynamicPricing=', dynamicPricing, 'fetchTrigger=', fetchTrigger);
        if (!dynamicPricing) return;

        const productIds = products.map((p) => p.id).filter(Boolean);
        if (productIds.length === 0) {
            setPricingLoaded(true);
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                console.log('[ProductGrid] Fetching pricing — POST /showroom/api/products/pricing', { productIds: productIds.length, productType, brand, fetchTrigger });
                const res = await fetch('/showroom/api/products/pricing', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'same-origin', // ensures cookies are sent
                    body: JSON.stringify({ productIds, productType, brand }),
                });

                if (cancelled) return;

                console.log('[ProductGrid] Pricing response status:', res.status);
                if (res.status === 401) {
                    // Not authenticated — keep tiles minimal (no pricing)
                    console.info('[ProductGrid] User not authenticated (401) — pricing hidden.');
                    setPricingDenied(true);
                    setPricingLoaded(true);
                    return;
                }

                const data = await res.json();
                console.log("for-testing-----------------")
                console.log('[ProductGrid] Pricing data:', data);
                console.log('[ProductGrid] Pricing data received — keys:', Object.keys(data.pricing || {}).length);
                if (!cancelled) {
                    setPricingDenied(false);
                    setPricingMap(data.pricing || {});
                }
            } catch (err) {
                console.error('[ProductGrid] Failed to fetch pricing:', err);
            } finally {
                if (!cancelled) setPricingLoaded(true);
            }
        })();

        return () => { cancelled = true; };
    }, [dynamicPricing, products, productType, brand, fetchTrigger]);

    // ── Re-fetch pricing after login ────────────────────────────────────
    useEffect(() => {
        if (!dynamicPricing) {
            console.log('[ProductGrid] dynamicPricing is false — skipping AUTH_LOGIN_EVENT listener');
            return;
        }

        console.log('[ProductGrid] Registering AUTH_LOGIN_EVENT listener (dynamicPricing=true, brand=' + brand + ')');

        const handleAuthLogin = () => {
            console.info('[ProductGrid] AUTH_LOGIN_EVENT received — re-fetching pricing. brand=' + brand + ' productType=' + productType);
            // Reset state so the UI shows a loading state while we re-fetch
            setPricingLoaded(false);
            setPricingDenied(false);
            setPricingMap({});
            setFetchTrigger((n) => n + 1);
        };

        window.addEventListener(AUTH_LOGIN_EVENT, handleAuthLogin);
        return () => {
            console.log('[ProductGrid] Removing AUTH_LOGIN_EVENT listener');
            window.removeEventListener(AUTH_LOGIN_EVENT, handleAuthLogin);
        };
    }, [dynamicPricing]);

    // ── Merge pricing into products for the current page ────────────────
    const pageProducts = useMemo(() => {
        const sliced = products && products.length > 0
            ? products.slice((currentPageNumber - 1) * productsPerPage, currentPageNumber * productsPerPage)
            : [];

        if (!dynamicPricing || !pricingLoaded || Object.keys(pricingMap).length === 0) return sliced;

        // Merge server pricing data into the minimal product objects
        return sliced.map((item: any) => {
            const pricing = pricingMap[item.id];
            if (!pricing) return item;
            return {
                ...item,
                price: pricing.partnerPricing?.calculated?.finalPrice || 0,
                comparePrice: pricing.partnerPricing?.calculated?.comparePrice || 0,
                priceRange: pricing.priceRange,
                expiresOn: pricing.expiresOn,
                model3dUrl: pricing.model3dUrl,
                viewInYourRoomLink: pricing.viewInYourRoomLink,
                otherFabrics: pricing.otherFabrics,
                configurationDisplayName: pricing.configurationDisplayName,
                configurationIcon: pricing.configurationIcon,
            };
        });
    }, [products, currentPageNumber, productsPerPage, dynamicPricing, pricingLoaded, pricingMap]);

    const handlePageChange = (_e: any, pageNumber: number) => {
        setCurrentPageNumber(pageNumber);
    };

    return (
        <FavoritesProvider>
            {({ userInfo, favorites, setFavorites }) => (
                <>
                    <Grid container spacing={2} className="productsContainer">
                        {pageProducts.map((item: any) => (
                            <Grid key={item.name || item.id} item xs={columns.xs} md={columns.md}>
                                <ProductCard
                                    card={item as any}
                                    favorites={favorites}
                                    setFavorites={setFavorites}
                                    customerId={userInfo?.cId}
                                    onClick={(e: any, card: any) => onProductClick?.(e, card)}
                                    height={productCardHeight}
                                    pageName={pageName}
                                    hidePricing={dynamicPricing && !pricingLoaded}
                                    showLoginOverlay={dynamicPricing && pricingLoaded && pricingDenied}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    <Divider style={{ margin: "20px" }} />
                    {products?.length > productsPerPage && (
                        <Grid container direction="row" justifyContent="center" alignItems="center">
                            <Pagination
                                count={Math.ceil(products.length / productsPerPage)}
                                variant="outlined"
                                shape="rounded"
                                onChange={handlePageChange}
                            />
                        </Grid>
                    )}
                </>
            )}
        </FavoritesProvider>
    );
};

export default ProductGrid;
